import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Users, Crown, UserPlus, LogOut, UserMinus, ArrowLeftRight, Check, X, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SectionHeader, DLButton, EmptyState, Slogan } from "@/components/dl/ui";
import EntitlementGate from "@/components/dl/EntitlementGate";
import { useAuth } from "@/context/AuthContext";

export default function DLTeam() {
  const { caseId, caseData, team, roster, isCaptain, invites, entitled, entitlement, reload } = useOutletContext();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [inviteId, setInviteId] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await reload(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!entitled) return <EntitlementGate entitlement={entitlement} />;

  // Not on a team yet
  if (!team) return (
    <div className="space-y-8">
      <SectionHeader kicker="Build Your Team" title="Assemble your defense" desc="One player belongs to one team per case. Create a team and invite your co-counsel, or accept a pending invite." />
      {invites && invites.length > 0 && (
        <div className="dl-panel rounded-lg p-5">
          <Slogan className="block mb-3">Pending invites</Slogan>
          <div className="space-y-2">
            {invites.map((inv) => (
              <div key={inv.membership_id} className="flex items-center justify-between gap-3 border border-white/10 rounded-md px-4 py-3" data-testid="dl-invite-row">
                <div className="flex items-center gap-2 text-white/80"><Mail className="w-4 h-4 text-gold" /> {inv.team_name || "A defense team"}</div>
                <div className="flex gap-2">
                  <DLButton variant="primary" disabled={busy} data-testid="dl-invite-accept" onClick={() => run(() => api.post(`/dl/teams/${inv.dl_team_id}/accept`), "You're on the team.")}><Check className="w-4 h-4" /> Accept</DLButton>
                  <DLButton variant="ghost" disabled={busy} data-testid="dl-invite-decline" onClick={() => run(() => api.post(`/dl/teams/${inv.dl_team_id}/decline`), "Invite declined.")}><X className="w-4 h-4" /> Decline</DLButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="dl-panel-gold rounded-lg p-6 max-w-lg">
        <Slogan className="block mb-3">Create a defense team</Slogan>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="dl-team-name-input"
          placeholder="Team name (e.g. The Reasonable Doubt)" maxLength={60}
          className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-white placeholder-white/30 focus:border-gold outline-none" />
        <div className="mt-4">
          <DLButton disabled={busy || !name.trim()} data-testid="dl-create-team-btn"
            onClick={() => run(() => api.post(`/dl/cases/${caseId}/teams`, { team_name: name.trim() }), "Team created. You're the Captain.")}>
            <Users className="w-4 h-4" /> Create Team
          </DLButton>
        </div>
        <p className="mt-3 text-xs text-white/40">Max {caseData.max_team_size || 5} players per team.</p>
      </div>
    </div>
  );

  // On a team
  const active = roster.filter((r) => r.status === "active");
  const invited = roster.filter((r) => r.status === "invited");

  return (
    <div className="space-y-8">
      <SectionHeader kicker="Your Team" title={team.team_name}
        right={isCaptain && <span className="inline-flex items-center gap-1 text-xs font-mono text-gold border border-gold/40 rounded-sm px-2 py-1"><Crown className="w-3.5 h-3.5" /> Captain</span>} />

      <div className="dl-panel rounded-lg divide-y divide-white/5">
        {active.map((m) => (
          <div key={m.membership_id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid="dl-roster-row">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold font-mono font-semibold">
                {(m.display_name || "?")[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-white font-medium truncate flex items-center gap-2">
                  {m.display_name || "Player"}
                  {m.role === "captain" && <Crown className="w-3.5 h-3.5 text-gold" />}
                  {m.user_id === user?.user_id && <span className="text-[10px] font-mono text-white/40">(you)</span>}
                </div>
                <div className="text-[11px] font-mono text-white/35">{m.role}</div>
              </div>
            </div>
            {isCaptain && m.user_id !== user?.user_id && (
              <div className="flex gap-2 shrink-0">
                <DLButton variant="ghost" disabled={busy} data-testid="dl-transfer-captain" onClick={() => run(() => api.post(`/dl/teams/${team.dl_team_id}/transfer-captain`, { user_id: m.user_id }), "Captaincy transferred.")}><ArrowLeftRight className="w-3.5 h-3.5" /></DLButton>
                <DLButton variant="danger" disabled={busy} data-testid="dl-remove-member" onClick={() => run(() => api.post(`/dl/teams/${team.dl_team_id}/remove`, { user_id: m.user_id }), "Member removed.")}><UserMinus className="w-3.5 h-3.5" /></DLButton>
              </div>
            )}
          </div>
        ))}
        {invited.map((m) => (
          <div key={m.membership_id} className="flex items-center justify-between gap-3 px-4 py-3 opacity-70" data-testid="dl-roster-invited">
            <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-white/40" /><span className="text-white/60">{m.display_name || m.user_id}</span></div>
            <span className="text-[10px] font-mono text-white/40 uppercase tracking-wider">Invited</span>
          </div>
        ))}
      </div>

      {isCaptain && active.length < (caseData.max_team_size || 5) && (
        <div className="dl-panel rounded-lg p-5 max-w-lg">
          <Slogan className="block mb-3">Invite a player</Slogan>
          <div className="flex gap-2">
            <input value={inviteId} onChange={(e) => setInviteId(e.target.value)} data-testid="dl-invite-input" type="email"
              placeholder="Teammate's email address"
              className="flex-1 bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-white placeholder-white/30 focus:border-gold outline-none" />
            <DLButton disabled={busy || !inviteId.trim()} data-testid="dl-invite-btn"
              onClick={() => run(async () => { await api.post(`/dl/teams/${team.dl_team_id}/invite-by-email`, { email: inviteId.trim() }); setInviteId(""); }, "Invite sent.")}>
              <UserPlus className="w-4 h-4" /> Invite
            </DLButton>
          </div>
          <p className="mt-2 text-xs text-white/40">We'll match the email to their existing HSTC account. Invitees must have an active Case Pass — invites never bypass entitlement.</p>
        </div>
      )}

      <div className="pt-2">
        <DLButton variant="danger" disabled={busy} data-testid="dl-leave-team"
          onClick={() => run(() => api.post(`/dl/teams/${team.dl_team_id}/leave`), "You left the team.")}>
          <LogOut className="w-4 h-4" /> Leave Team
        </DLButton>
        {isCaptain && active.length > 1 && <p className="mt-2 text-xs text-white/40">Transfer captaincy before you can leave.</p>}
      </div>
    </div>
  );
}
